import { useEffect, useMemo, useState } from 'react';

import type { ForecastPayload } from '@/types/Forecast';
import { pickCurrentGroup, pickHorizonItems } from '@/utils/forecast';

interface UseForecastOptions {
  horizons?: number[];
  nowMs?: number;
}

export function useForecast({ horizons = [3600, 7200, 10800, 14400], nowMs = Date.now() }: UseForecastOptions = {}) {
  const [payload, setPayload] = useState<ForecastPayload | null>(null);
  const url = '/data/forecast_latest.json';
  useEffect(() => {
    let cancelled = false;

    fetch(url, { cache: 'no-store' })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return response.json();
      })
      .then((json: ForecastPayload) => {
        if (!cancelled) setPayload(json);
      })
      .catch(() => {
        if (!cancelled) setPayload(null);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  const currentGroup = useMemo(
    () => (payload?.groupsByTimestamp ? pickCurrentGroup(payload.groupsByTimestamp, nowMs) : null),
    [payload, nowMs],
  );

  const horizonItems = useMemo(() => pickHorizonItems(currentGroup, horizons), [currentGroup, horizons]);

  return { horizonItems };
}
